import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, User } from 'lucide-react';
import { Button, Card, CardTitle, PageHeader, PageSpinner } from '@/components/ui';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import type { ResponseEnvelope } from '@/types/api';
import { Endpoints } from '@/lib/api/endpoints';

interface UserProfile {
  userId: string;
  username: string;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  roles: string[];
  clientId?: string;
  accountStatus: string;
}

const ROLE_COLOR: Record<string, string> = {
  AUTHMATECH_SUPER_ADMIN: 'bg-rose-500/15 text-rose-400',
  AUTHMATECH_ADMIN: 'bg-violet-500/15 text-violet-400',
  AUTHMATECH_OPERATOR: 'bg-blue-500/15 text-blue-400',
  AUTHMATECH_SUPPORT: 'bg-cyan-500/15 text-cyan-400',
  AUTHMATECH_FINANCE: 'bg-amber-500/15 text-amber-400',
  AUTHMATECH_VIEWER: 'bg-surface-muted text-fg-muted',
  AUTHMATECH_CLIENT_ADMIN: 'bg-emerald-500/15 text-emerald-400',
  AUTHMATECH_CLIENT_OPERATOR: 'bg-blue-500/15 text-blue-400',
  AUTHMATECH_CLIENT_FINANCE: 'bg-amber-500/15 text-amber-400',
  AUTHMATECH_CLIENT_VIEWER: 'bg-surface-muted text-fg-muted',
};

function formatRole(role: string): string {
  return role
    .replace(/^AUTHMATECH_/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ProfilePage() {
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const profileQuery = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: async () =>
      unwrap(await api.get<ResponseEnvelope<UserProfile>>(Endpoints.meProfile.get)),
    staleTime: 60_000,
  });

  const profile = profileQuery.data;

  const updateMutation = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.put<ResponseEnvelope<{ message: string }>>(Endpoints.meProfile.update, {
          firstName: firstName || profile?.firstName,
          lastName: lastName || profile?.lastName,
        }),
      ),
    onSuccess: () => {
      setSaved(true);
      setError(null);
      qc.invalidateQueries({ queryKey: ['me', 'profile'] });
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: unknown) => {
      setError(isApiError(err) ? err.messages[0]?.message ?? 'Update failed' : 'Update failed');
    },
  });

  if (profileQuery.isLoading) return <PageSpinner />;

  const initials = profile
    ? `${profile.firstName?.[0] ?? ''}${profile.lastName?.[0] ?? ''}`.toUpperCase() ||
      profile.username?.[0]?.toUpperCase() ||
      'U'
    : 'U';

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader title="My Profile" subtitle="Manage your account details" />

      {/* Avatar + identity */}
      <Card>
        <div className="flex items-center gap-4">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-2xl text-2xl font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)' }}
          >
            {initials}
          </div>
          <div>
            <p className="text-lg font-semibold text-fg">
              {profile?.firstName || profile?.lastName
                ? `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim()
                : profile?.username}
            </p>
            <p className="text-sm text-fg-muted">{profile?.email}</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {profile?.roles.map((r) => (
                <span
                  key={r}
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ROLE_COLOR[r] ?? 'bg-surface-muted text-fg-muted'}`}
                >
                  {formatRole(r)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Editable fields */}
      <Card>
        <CardTitle className="mb-4">
          <span className="flex items-center gap-2">
            <User className="h-4 w-4 text-fg-muted" />
            Personal Details
          </span>
        </CardTitle>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-fg-muted">
                First Name
              </label>
              <input
                type="text"
                value={firstName || profile?.firstName || ''}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={profile?.firstName ?? 'First name'}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-fg-muted">
                Last Name
              </label>
              <input
                type="text"
                value={lastName || profile?.lastName || ''}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={profile?.lastName ?? 'Last name'}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-fg-muted">
              Email
            </label>
            <input
              type="email"
              value={profile?.email ?? ''}
              readOnly
              className="w-full cursor-not-allowed rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted"
            />
            <p className="mt-1 text-xs text-fg-subtle">Email is managed by your identity provider.</p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-fg-muted">
              Username
            </label>
            <input
              type="text"
              value={profile?.username ?? ''}
              readOnly
              className="w-full cursor-not-allowed rounded-lg border border-border bg-surface-muted px-3 py-2 font-mono text-sm text-fg-muted"
            />
          </div>

          {profile?.clientId && (
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-fg-muted">
                Client ID
              </label>
              <input
                type="text"
                value={profile.clientId}
                readOnly
                className="w-full cursor-not-allowed rounded-lg border border-border bg-surface-muted px-3 py-2 font-mono text-sm text-fg-muted"
              />
            </div>
          )}

          {error && (
            <p className="rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-400">{error}</p>
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-400">
                <Check className="h-4 w-4" />
                Saved
              </span>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
